import type { LabelPrintMarginSettings } from "@itaas/label-print";
import type { LabelPrintFormat } from "@/core/printing/label-print-formats";
import type { LabelPrintRotation } from "@/core/printing/label-print-rotation";
import { renderLabelPrintBitmap } from "@/core/printing/label-print-canvas";
import { printLabelViaWebSerial } from "@/core/printing/label-print-tspl";
import {
  printLabelViaBridge,
  probePrintBridge,
} from "@/core/printing/print-bridge-client";
import type { ProductLabelData } from "./ProductLabelPrintSheet.types";

export type LabelPrintMode = "auto" | "bitmap" | "tspl" | "bridge";

export interface PrintLabelOptions {
  format?: LabelPrintFormat;
  rotation?: LabelPrintRotation;
  margins?: LabelPrintMarginSettings;
  mode?: LabelPrintMode;
  printerName?: string;
  onPrinted?: () => void;
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function buildBitmapPrintHtml(bitmap: ReturnType<typeof renderLabelPrintBitmap>): string {
  const { dataUrl, pageWidthMm, pageHeightMm, widthPx, heightPx } = bitmap;

  return `<!DOCTYPE html>
<html lang="fr">
<head>
  <meta charset="utf-8" />
  <title>Étiquette</title>
  <style>
    @page {
      size: ${pageWidthMm}mm ${pageHeightMm}mm;
      margin: 0;
    }
    html, body {
      margin: 0;
      padding: 0;
      width: ${pageWidthMm}mm;
      height: ${pageHeightMm}mm;
      overflow: hidden;
      background: #fff;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    img {
      display: block;
      width: ${pageWidthMm}mm;
      height: ${pageHeightMm}mm;
      max-width: none;
      max-height: none;
      object-fit: fill;
    }
  </style>
</head>
<body>
  <img
    src="${dataUrl}"
    width="${widthPx}"
    height="${heightPx}"
    alt="${escapeHtml("Étiquette produit")}"
  />
</body>
</html>`;
}

export function printLabelDocument(
  label: ProductLabelData,
  format: LabelPrintFormat,
  rotation: LabelPrintRotation = 0,
  onPrinted?: () => void,
): void {
  const bitmap = renderLabelPrintBitmap(label, format, rotation);

  const iframe = document.createElement("iframe");
  iframe.setAttribute("title", "Impression étiquette");
  iframe.setAttribute("aria-hidden", "true");
  iframe.style.cssText =
    "position:fixed;left:-10000px;top:0;width:90mm;height:90mm;border:0;opacity:0;pointer-events:none";

  document.body.appendChild(iframe);

  const doc = iframe.contentDocument;
  const win = iframe.contentWindow;
  if (!doc || !win) {
    iframe.remove();
    return;
  }

  doc.open();
  doc.write(buildBitmapPrintHtml(bitmap));
  doc.close();

  const cleanup = () => {
    iframe.remove();
    win.removeEventListener("afterprint", onAfterPrint);
    onPrinted?.();
  };

  const onAfterPrint = () => cleanup();

  win.addEventListener("afterprint", onAfterPrint);

  const triggerPrint = () => {
    win.focus();
    win.print();
    window.setTimeout(() => {
      if (document.body.contains(iframe)) {
        cleanup();
      }
    }, 5000);
  };

  if (doc.readyState === "complete") {
    window.requestAnimationFrame(() => window.requestAnimationFrame(triggerPrint));
  } else {
    iframe.addEventListener("load", () => {
      window.requestAnimationFrame(() => window.requestAnimationFrame(triggerPrint));
    });
  }
}

export async function printLabelDocumentDirect(
  label: ProductLabelData,
  format: LabelPrintFormat,
  rotation: LabelPrintRotation = 0,
): Promise<void> {
  await printLabelViaWebSerial(label, format, rotation);
}

export async function printLabelDocumentBridge(
  label: ProductLabelData,
  format: LabelPrintFormat,
  rotation: LabelPrintRotation = 0,
  printerName?: string,
  margins?: LabelPrintMarginSettings,
): Promise<void> {
  await printLabelViaBridge(label, format, rotation, printerName, margins);
}

export async function resolveAutoPrintMode(): Promise<Exclude<LabelPrintMode, "auto">> {
  const bridge = await probePrintBridge();
  if (bridge?.ok) return "bridge";
  return "bitmap";
}
